import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { type EventLogType } from '@/api/eventLog';
import { CopyToClipboardText } from '@/components/Form';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { USDateTimeViewFormat } from '@/constants/formats';
import type SolAPIError from '@/error/SolAPIError';
import { EventTypeFilter } from '@/features/filters';
import { truncateMiddle } from '@/helper/string-util';
import { ROUTES } from '@/router';

type Props = {
  data?: EventLogType[];
  totalResults?: number;
  isLoading?: boolean;
  error: SolAPIError | null;
};

const EventLogTable = ({ data, totalResults, isLoading, error }: Props) => {
  const sortFields = ROUTES.eventLog.searchParams['e.sortBy'].enumValues;

  const columns = useMemo<InstantSearchDataTableColumnDef<EventLogType>[]>(
    () => [
      {
        header: t`Source`,
        id: 'sourceString',
        accessorFn: (row) => (typeof row.source === 'string' ? row.source : ''),
        size: 200,
        enableSorting: sortFields.includes('sourceString'),
        minSize: 140,
        flex: 1,
        cell: ({ row }) => row.original.source,
      },
      {
        header: t`Type`,
        id: 'typeString',
        accessorKey: 'type',
        size: 100,
        enableSorting: sortFields.includes('typeString'),
        filterComponent: (props) => <EventTypeFilter {...props} />,
        cell: ({ row }) => row.original.type,
      },
      {
        header: t`Details`,
        id: 'detailsString',
        minSize: 200,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.details,
      },
      {
        header: t`Log ID`,
        id: 'id',
        size: 200,
        cell: ({ row }) => (
          <div className="-my-2">
            <CopyToClipboardText variant="caption1" value={row.original.id}>
              {truncateMiddle(row.original.id, 24)}
            </CopyToClipboardText>
          </div>
        ),
      },
      {
        header: t`Date/Time`,
        id: 'occurredAt',
        accessorKey: 'date',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('occurredAt'),
        size: 140,
        cell: ({ row }) => dayjs(row.original.date).format(USDateTimeViewFormat),
      },
    ],
    [sortFields],
  );

  return (
    <InstantSearchDataTable<EventLogType>
      columns={columns}
      data={data}
      totalResults={totalResults}
      loading={isLoading}
      emptyMessage={<Trans>No event log activity found.</Trans>}
      error={error}
      getRowId={(record) => record.id}
    />
  );
};

export default EventLogTable;
